/**
 * Location Helper Utility
 * Handles browser geolocation API and city detection
 */

import { setCustomerPosition } from "./customerPosition";

/**
 * Check if geolocation is supported by the browser
 * @returns {boolean}
 */
export const isGeolocationSupported = () => {
  return 'geolocation' in navigator;
};

/**
 * Get current position using browser's Geolocation API
 * @param {Object} options - Geolocation options
 * @returns {Promise<{lat: number, lng: number}>}
 */
export const getCurrentPosition = (options = {}) => {
  return new Promise((resolve, reject) => {
    if (!isGeolocationSupported()) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    const defaultOptions = {
      enableHighAccuracy: false,
      timeout: 10000,
      maximumAge: 0,
      ...options,
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Remember it (rounded to ~1 km) so search can label each pickup point
        // with its road distance. Captured HERE rather than at the three
        // callers of this function, so none of them can forget to.
        //
        // Nothing else changes: the caller still gets the exact coordinates it
        // asked for and still uses them only to detect the city.
        setCustomerPosition(position.coords.latitude, position.coords.longitude);
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => {
        let errorMessage = 'Unable to retrieve your location';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location permissions.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
          default:
            errorMessage = 'An unknown error occurred while detecting location.';
        }
        
        reject(new Error(errorMessage));
      },
      defaultOptions
    );
  });
};

/**
 * Detect nearest city based on GPS coordinates
 * Note: This function should be called from components that have access to usefetchDataFromApi
 * For direct use, import and call usefetchDataFromApi from '@/utils/api'
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {Object} filters - Optional filters (longRentStatus)
 * @returns {Promise<Object>} - API response with city data
 */
export const detectCityByCoordinates = async (lat, lng, filters = {}) => {
  try {
    // Use native fetch with full URL since this is a utility function
    const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL;
    const response = await fetch(`${baseUrl}/data/detect-city`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        lat,
        lng,
        ...filters,
      }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    throw new Error('Failed to detect city from coordinates');
  }
};

/**
 * Detect city using IP address (no coordinates needed)
 * @param {Object} filters - Optional filters (longRentStatus)
 * @returns {Promise<Object>} - API response with city data
 */
export const detectCityByIP = async (filters = {}) => {
  try {
    // Use native fetch with full URL since this is a utility function
    const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL;
    const response = await fetch(`${baseUrl}/data/detect-city`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(filters),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    throw new Error('Failed to detect city from IP');
  }
};

/**
 * Auto-detect city with GPS fallback to IP
 * @param {Object} filters - Optional filters (longRentStatus)
 * @returns {Promise<{city: Object|null, source: string, message: string}>}
 */
export const autoDetectCity = async (filters = {}) => {
  try {
    // Try GPS first
    const coords = await getCurrentPosition();
    const response = await detectCityByCoordinates(coords.lat, coords.lng, filters);
    
    if (response.status === 1 && response.data) {
      return {
        city: response.data,
        source: 'gps',
        message: response.message || 'Location detected successfully',
      };
    }
    
    // GPS didn't return a city, try IP
    return await fallbackToIP(filters);
  } catch (error) {
    // GPS failed, try IP fallback
    return await fallbackToIP(filters);
  }
};

/**
 * Fallback to IP-based detection
 * @param {Object} filters - Optional filters
 * @returns {Promise<{city: Object|null, source: string, message: string}>}
 */
const fallbackToIP = async (filters = {}) => {
  try {
    const response = await detectCityByIP(filters);
    
    if (response.status === 1 && response.data) {
      return {
        city: response.data,
        source: 'ip',
        message: response.message || 'Location detected from IP (approximate)',
      };
    }
    
    // IP detection also failed
    return {
      city: null,
      source: 'none',
      message: 'Please select your city from the dropdown',
    };
  } catch (error) {
    return {
      city: null,
      source: 'none',
      message: 'Please select your city from the dropdown',
    };
  }
};

